import { applyTranslation, type TranslationRow } from '@w/content-blocks'
import {
  getTextFromChars,
  type Block,
  type Blocks,
} from '@w/content-blocks/src/block'
import { getTrainingPromptBlock } from '../training/training-scoring'

export type CardType = 'sa' | 'la' | 'sc' | 'mc' | 'cloze' | 'sr'

/**
 * The card type shown in the Cards table. Derived from the page block the
 * flashcard's qid points at: a cloze block is 'cloze', otherwise the
 * question block's subtype (sa/la/sc/mc/sr).
 */
export function getCardType(blocks: Block[], qid: string): CardType | null {
  const block = getTrainingPromptBlock(blocks, qid)
  if (!block) return null
  if (block.type === 'cloze') return 'cloze'
  return block.subtype
}

/**
 * The content shown to the learner when the card is presented — the *question*,
 * not surrounding notes. For sa/la/sc/mc/sr that is the question prompt. For
 * a cloze it is the sentence the blank sits in, with the hidden answer rendered
 * as "____" so the table mirrors what the learner sees during training.
 */
export function getCardContent(source: Block[], qid: string, rows: TranslationRow[] = []): string {
  const blocks = applyTranslation(source, rows)
  const block = getTrainingPromptBlock(blocks, qid)
  if (!block) return ''

  if (block.type === 'cloze') {
    const sentence = _findClozeSentence(blocks, qid)
    return collapse(sentence ?? _plainText(block.children))
  }

  // Every question subtype carries its prompt in `question`.
  if (block.question && block.question.length > 0) {
    return collapse(_plainText(block.question))
  }
  return ''
}

const CLOZE_BLANK = '____'

/**
 * Render the paragraph that contains the cloze as plain text, replacing the
 * target cloze's answer with a blank and other clozes with their plain text.
 */
function _findClozeSentence(blocks: Block[], qid: string): string | null {
  for (const block of blocks) {
    if (_directlyContainsCloze(block, qid)) {
      return _plainTextWithClozeBlank(block, qid)
    }
    if ('children' in block && Array.isArray(block.children)) {
      const nested = _findClozeSentence(block.children, qid)
      if (nested !== null) return nested
    }
  }
  return null
}

function _directlyContainsCloze(block: Block, qid: string): boolean {
  if (!('children' in block) || !Array.isArray(block.children)) return false
  return block.children.some(
    (child) => child.type === 'cloze' && child.qid === qid,
  )
}

function _plainTextWithClozeBlank(block: Block, qid: string): string {
  if (block.type === 'cloze') {
    return block.qid === qid ? CLOZE_BLANK : _plainText(block.children)
  }
  if ('children' in block && Array.isArray(block.children)) {
    return block.children
      .map((child) => _plainTextWithClozeBlank(child, qid))
      .join('')
  }
  return _plainTextBlock(block)
}

function collapse(text: string): string {
  return text.trim().replaceAll(/\s+/g, ' ')
}

function _plainText(blocks: Blocks): string {
  return blocks.map((block) => _plainTextBlock(block)).join('')
}

function _plainTextBlock(block: Block): string {
  if (block.type === 'text') return block.text
  if (block.type === 'chars') return getTextFromChars(block)
  if (block.type === 'br') return ' '
  if (block.type === 'mention') {
    return block.subtype === 'date'
      ? new Date(block.date).toLocaleDateString()
      : `@${block.uid}`
  }
  return 'children' in block && Array.isArray(block.children)
    ? _plainText(block.children)
    : ''
}
